import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FileText, Download, CalendarOff, Loader2, Check, X, History, HeartPulse, Send, Paperclip, Camera, AlarmClockCheck, Receipt, Upload, Trash2, Plus, Minus, Palmtree } from "lucide-react";
import api from "@/lib/api";
import { postOrQueue } from "@/lib/offlineQueue";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLiveTick } from "@/hooks/useLiveTick";
import RipOutFormDialog from "@/components/dashboard/RipOutFormDialog";
import ReceiptViewer from "@/components/dashboard/ReceiptViewer";
import SignaturePad from "@/components/dashboard/SignaturePad";

const fmtDay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};
const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: "border-amber-500/50 bg-amber-500/15 text-amber-800 dark:text-amber-300",
    approved: "border-emerald-500/50 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300",
    denied: "border-red-500/50 bg-red-500/15 text-red-800 dark:text-red-300",
    confirmed: "border-emerald-500/50 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300",
    corrected: "border-[#1a3fd1]/50 bg-[#1a3fd1]/15 text-[#2c5bf0] dark:text-[#8fa4f0]",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${map[status] || map.pending}`} data-testid={`req-status-${status}`}>
      {status}
    </span>
  );
};

/**
 * RequestsTab — every role's self-service counter: download an employment
 * letter (regenerated fresh each time), request time off, and track request
 * status. Admins/owners also get the pending approval queue, the resolved
 * history, and the employer punch-request log.
 */
// Partial-day time choices: 5:00 AM – 8:00 PM in 30-min steps, labeled 12h
// (native <input type="time"> renders 24-hour on many Android devices).
const TIME_OPTS = [];
for (let h = 5; h <= 20; h++) for (const m of [0, 30]) TIME_OPTS.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
const fmtTime12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

/** Request card that starts collapsed — + expands, − collapses. `count`
 * shows a red pending-approvals badge so admins spot work without opening. */
const CollapsibleCard = ({ icon, iconClass, title, subtitle, testid, count, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
        className="flex w-full items-center gap-3 text-left"
      >
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md border ${iconClass}`}>{icon}</div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">{subtitle}</p>
        </div>
        {count > 0 && (
          <span className="shrink-0 rounded-full bg-red-600 px-2 py-0.5 font-mono text-[10px] font-bold text-white" data-testid={`${testid}-pending-count`}>
            {count}
          </span>
        )}
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/15 bg-white/5 text-white/70" data-testid={`${testid}-toggle-icon`}>
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};

const RequestsTab = () => {
  const { user } = useAuth();
  const isAdmin = ["owner", "admin", "superadmin", "manager"].includes(user?.role) || !!user?.manage_users;
  // The employment-letter generator is owner/admin ONLY — managers get the
  // approval queues but not this card (backend 403s them on it anyway).
  const canGenLetters = ["owner", "admin", "superadmin"].includes(user?.role) || !!user?.manage_users;
  const [reason, setReason] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [mine, setMine] = useState([]);
  const [queue, setQueue] = useState([]);
  const [punchLog, setPunchLog] = useState([]);
  const [otQueue, setOtQueue] = useState([]);
  const [benefits, setBenefits] = useState(null);
  // Feature flags — health benefits can be switched off in Settings; when
  // off, the claim card below shows a "not currently being offered" note.
  const [features, setFeatures] = useState(null);
  // Health claim form state
  const [hcName, setHcName] = useState(user?.name || "");
  const [hcDate, setHcDate] = useState("");
  const [hcDesc, setHcDesc] = useState("");
  const [hcAmount, setHcAmount] = useState("");
  const [hcReceipt, setHcReceipt] = useState(null);
  const [hcBusy, setHcBusy] = useState(false);
  const [hcSig, setHcSig] = useState("");
  const [hcSigKey, setHcSigKey] = useState(1);   // bump to reset the pad after a submit
  // General request box
  const [genMsg, setGenMsg] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  // Vacation pay payout request (needs a linked employee)
  const [vacInfo, setVacInfo] = useState(null); // null = loading; {unavailable:true} = no linked employee
  const [vacQueue, setVacQueue] = useState([]); // team's pending vacation-pay payout requests (reviewers only)
  const [vacAmt, setVacAmt] = useState("");
  const [vacMode, setVacMode] = useState("dollars"); // "dollars" | "hours" — one or the other, never both
  const [vacBusy, setVacBusy] = useState(false);
  // Expense reimbursement form state
  const [exJobs, setExJobs] = useState([]);
  const [exProjectId, setExProjectId] = useState("");
  const [exDesc, setExDesc] = useState("");
  const [exAmount, setExAmount] = useState("");
  const [exDate, setExDate] = useState(() => new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" }));
  const [exReceipt, setExReceipt] = useState(null);
  const [exScanning, setExScanning] = useState(false);
  const [exScanned, setExScanned] = useState(false);
  const [exBusy, setExBusy] = useState(false);
  const [exMine, setExMine] = useState([]);
  const [exQueue, setExQueue] = useState([]);
  const [exReceiptSrc, setExReceiptSrc] = useState(null);
  const [exDetail, setExDetail] = useState(null);        // request open in the details dialog
  const [exDetailReceipt, setExDetailReceipt] = useState(undefined); // undefined = loading
  const isSuper = user?.role === "superadmin";
  // Admin: letter for any employee
  const [employees, setEmployees] = useState([]);
  const [letterEmp, setLetterEmp] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/time-off/mine");
      setMine(Array.isArray(data) ? data : []);
    } catch { setMine([]); }
    // Benefit balance for the health-claim card (needs a linked employee).
    try {
      const { data } = await api.get("/me/benefits");
      setBenefits(data);
    } catch { setBenefits(null); }
    // Jobs for the expense form dropdown + my past expense submissions.
    try {
      const { data } = await api.get("/projects");
      setExJobs((Array.isArray(data) ? data : []).filter((p) => (p.status || "Active") === "Active" || p.status === "In Progress"));
    } catch { setExJobs([]); }
    try {
      const { data } = await api.get("/expense-requests/mine");
      setExMine(Array.isArray(data) ? data : []);
    } catch { setExMine([]); }
    try {
      const { data } = await api.get("/settings/features");
      setFeatures(data);
    } catch { setFeatures({ health_benefits_enabled: true }); } // on error, default to ON so the card isn't hidden forever
    // Vacation balance + my payout requests (needs a linked employee).
    try {
      const { data } = await api.get("/vacation-requests/mine");
      setVacInfo(data);
    } catch { setVacInfo({ unavailable: true }); }
    if (isAdmin) {
      try {
        const { data } = await api.get("/time-off");
        setQueue(Array.isArray(data) ? data : []);
      } catch { setQueue([]); }
      try {
        const { data } = await api.get("/punch-requests");
        setPunchLog(Array.isArray(data) ? data : []);
      } catch { setPunchLog([]); }
      // Everyone's pending vacation-pay payout requests — owner/admin/superadmin
      // only (the backend refuses managers), so don't ask for managers.
      if (canGenLetters) {
        try {
          const { data } = await api.get("/vacation-requests");
          setVacQueue(Array.isArray(data) ? data : []);
        } catch { setVacQueue([]); }
      }
      try {
        const { data } = await api.get("/overtime-requests");
        setOtQueue(Array.isArray(data) ? data : []);
      } catch { setOtQueue([]); }
      try {
        const { data } = await api.get("/expense-requests");
        setExQueue(Array.isArray(data) ? data : []);
      } catch { setExQueue([]); }
      try {
        const { data } = await api.get("/employees");
        const list = Array.isArray(data) ? data : (data?.employees || []);
        setEmployees(list.filter((e) => (e.status || "Active") !== "Inactive"));
      } catch { setEmployees([]); }
    }
  }, [isAdmin, canGenLetters]);
  useEffect(() => { load(); }, [load]);
  useLiveTick(load); // impersonation live-watch: refetch on every tick

  const submitTimeOff = async () => {
    if (!reason.trim()) { toast.error("Add a reason"); return; }
    if (!dateFrom) { toast.error("Pick a date"); return; }
    if ((timeFrom && !timeTo) || (!timeFrom && timeTo)) { toast.error("Pick both times for a partial day, or neither"); return; }
    setBusy(true);
    try {
      await api.post("/time-off", {
        reason: reason.trim(),
        date_from: dateFrom,
        date_to: dateTo || dateFrom,
        ...(timeFrom && timeTo ? { time_from: timeFrom, time_to: timeTo } : {}),
      });
      setReason(""); setDateFrom(""); setDateTo(""); setTimeFrom(""); setTimeTo("");
      toast.success("Time-off request sent to your admin");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the request");
    } finally { setBusy(false); }
  };

  const resolve = async (req, approve) => {
    setBusy(true);
    try {
      await api.post(`/time-off/${req.request_id}/resolve`, { approve });
      toast.success(approve ? `Approved — ${req.employee_name} is booked off` : "Request denied");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve the request");
    } finally { setBusy(false); }
  };

  const resolveOt = async (req, approve) => {
    setBusy(true);
    try {
      await api.post(`/time-entries/overtime/${req.request_id}/resolve`, { approve });
      toast.success(approve ? `Overtime approved — ${req.employee_name} can clock back in` : "Overtime denied");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve the request");
    } finally { setBusy(false); }
  };

  const downloadLetterFor = async (empId) => {
    setDownloading(true);
    try {
      const url = empId ? `/me/employment-letter.pdf?employee_id=${empId}` : "/me/employment-letter.pdf";
      const resp = await api.get(url, { responseType: "blob" });
      const objUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      // Use the server's per-employee filename (employment_letter_<name>.pdf)
      // so saved files are identifiable at a glance — and so a wrong-person
      // letter can never hide behind a generic filename.
      const cd = resp.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = m ? m[1] : "employment-letter.pdf";
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success("Employment letter downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the letter — is the account linked to an employee?");
    } finally { setDownloading(false); }
  };

  const submitHealthClaim = async () => {
    if (!hcDate) { toast.error("Pick the service date"); return; }
    if (!hcDesc.trim()) { toast.error("Add a description"); return; }
    if (!(parseFloat(hcAmount) > 0)) { toast.error("Enter the amount"); return; }
    if (!hcSig) { toast.error("Sign the form — draw your signature in the box"); return; }
    setHcBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", hcName.trim());
      fd.append("service_date", hcDate);
      fd.append("description", hcDesc.trim());
      fd.append("amount", String(parseFloat(hcAmount)));
      fd.append("signature", hcSig);
      if (hcReceipt) fd.append("receipt", hcReceipt);
      const res = await postOrQueue("/health-claim", fd, "health claim");
      setHcName(user?.name || ""); setHcDate(""); setHcDesc(""); setHcAmount(""); setHcReceipt(null); setHcSig(""); setHcSigKey((k) => k + 1);
      if (res.queued) { toast.warning("No signal — your claim is saved and will be submitted when you're back online"); return; }
      const { data } = res;
      toast.success(
        (data.emailed || []).length > 0
          ? "Claim submitted — completed form emailed to the office"
          : "Claim submitted — email will go out once an office mailbox is connected",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit the claim");
    } finally { setHcBusy(false); }
  };

  const submitVacation = async () => {
    const n = parseFloat(vacAmt);
    if (!n || n <= 0) { toast.error(vacMode === "hours" ? "Enter the hours" : "Enter an amount"); return; }
    setVacBusy(true);
    try {
      await api.post("/vacation-requests", vacMode === "hours" ? { hours: n } : { amount: n });
      toast.success("Vacation pay requested — it'll be flagged in yellow on that week's payroll");
      setVacAmt("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit the request");
    } finally { setVacBusy(false); }
  };

  const cancelVacation = async (id) => {
    try {
      await api.delete(`/vacation-requests/${id}`);
      toast.success("Request cancelled");
      load();
    } catch { toast.error("Could not cancel the request"); }
  };

  const submitGeneral = async () => {
    if (!genMsg.trim()) { toast.error("Type your request first"); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post("/general-request", { message: genMsg.trim() });
      setGenMsg("");
      toast.success(
        (data.emailed || []).length > 0
          ? "Request sent to the office"
          : "Request saved — email will go out once an office mailbox is connected",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the request");
    } finally { setGenBusy(false); }
  };

  const todayToronto = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });

  // AI receipt pre-fill: the moment a receipt is picked (upload or camera),
  // Claude Vision reads it and fills description/price/date — but never
  // clobbers anything the user already typed.
  const pickExpenseReceipt = (file) => {
    const f = file || null;
    setExReceipt(f);
    setExScanned(false);
    if (!f) return;
    (async () => {
      setExScanning(true);
      try {
        const fd = new FormData();
        fd.append("receipt", f);
        const { data } = await api.post("/expense-requests/scan-receipt", fd);
        const desc = [data.vendor, data.summary].filter(Boolean).join(" — ");
        let filled = false;
        if (desc) { setExDesc((cur) => (cur.trim() ? cur : (filled = true, desc))); }
        if (data.total) { setExAmount((cur) => (cur ? cur : (filled = true, String(data.total)))); }
        if (data.date) { setExDate((cur) => (cur && cur !== todayToronto() ? cur : (filled = true, data.date))); }
        if (filled) {
          setExScanned(true);
          toast.success("Receipt read — double-check the details before sending");
        }
      } catch { /* manual entry still works */ }
      finally { setExScanning(false); }
    })();
  };

  const submitExpense = async () => {
    if (!exDesc.trim()) { toast.error("Add a description"); return; }
    if (!(parseFloat(exAmount) > 0)) { toast.error("Enter the price"); return; }
    if (!exDate) { toast.error("Pick the date"); return; }
    if (!exProjectId) { toast.error("Select the job this expense was for"); return; }
    if (!exReceipt) { toast.error("Add a photo of the receipt"); return; }
    setExBusy(true);
    try {
      const fd = new FormData();
      fd.append("description", exDesc.trim());
      fd.append("amount", String(parseFloat(exAmount)));
      fd.append("date", exDate);
      fd.append("project_id", exProjectId);
      fd.append("receipt", exReceipt);
      const res = await postOrQueue("/expense-requests", fd, "expense");
      setExDesc(""); setExAmount(""); setExProjectId(""); setExReceipt(null); setExScanned(false);
      setExDate(todayToronto());
      if (res.queued) { toast.warning("No signal — your expense is saved and will be submitted when you're back online"); return; }
      const { data } = res;
      toast.success(
        (data.emailed || []).length > 0
          ? "Expense submitted — the office has been emailed for reimbursement"
          : "Expense submitted — email will go out once an office mailbox is connected",
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit the expense");
    } finally { setExBusy(false); }
  };

  const resolveExpense = async (req, approve) => {
    setBusy(true);
    try {
      await api.post(`/expense-requests/${req.request_id}/resolve`, { approve });
      toast.success(approve ? `Approved — added to ${req.project_name || "the job"}` : "Expense denied");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve the expense");
    } finally { setBusy(false); }
  };

  const viewExpenseReceipt = async (req) => {
    try {
      const { data } = await api.get(`/expense-requests/${req.request_id}/receipt`);
      setExReceiptSrc(data.receipt_image);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the receipt");
    }
  };

  // clicking any expense row (pending or history) opens its full details +
  // receipt; superadmin can stamp an approved request paid from there
  const openExpenseDetail = async (r) => {
    setExDetail(r);
    setExDetailReceipt(undefined);
    try {
      const { data } = await api.get(`/expense-requests/${r.request_id}/receipt`);
      setExDetailReceipt(data.receipt_image);
    } catch {
      setExDetailReceipt(null); // no receipt on file
    }
  };

  const markExpensePaid = async (r, paid) => {
    setBusy(true);
    try {
      await api.post(`/expense-requests/${r.request_id}/paid`, { paid });
      setExQueue((q) => q.map((x) => (x.request_id === r.request_id ? { ...x, paid, paid_at: paid ? new Date().toISOString() : null } : x)));
      setExDetail((d) => (d ? { ...d, paid, paid_at: paid ? new Date().toISOString() : null } : d));
      toast.success(paid ? `${r.employee_name}'s $${Number(r.amount).toFixed(2)} marked as paid` : "Paid stamp removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the paid stamp");
    } finally { setBusy(false); }
  };

  const pending = queue.filter((r) => r.status === "pending");
  const history = queue.filter((r) => r.status !== "pending");

  return (
    <div className="space-y-4" data-testid="requests-tab">
      {/* Employment letter */}
      <CollapsibleCard
        testid="letter-card"
        icon={<FileText className="h-5 w-5" />}
        iconClass="border-[#1a3fd1]/40 bg-[#1a3fd1]/10 text-[#4f6fe8]"
        title="Employment letter"
        subtitle="Generated fresh each download — today's date, your wage & status"
      >
        <button
          onClick={() => downloadLetterFor("")}
          disabled={downloading}
          data-testid="download-employment-letter"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60"
        >
          {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download PDF
        </button>
      </CollapsibleCard>

      {/* Request time off */}
      <CollapsibleCard
        testid="timeoff-card"
        icon={<CalendarOff className="h-5 w-5" />}
        iconClass="border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300"
        title="Request time off"
        subtitle="Your admin approves or denies"
      >
        <div className="space-y-3">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (e.g. dentist appointment, family trip…)"
            data-testid="timeoff-reason"
            className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="timeoff-from"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">To (optional)</label>
              <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} data-testid="timeoff-to"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
          </div>
          {dateFrom && (!dateTo || dateTo === dateFrom) && (
            <div data-testid="timeoff-partial">
              <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-white/50">
                Partial day? Add times (optional) — e.g. 1 hour off for an appointment
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/40">From time</label>
                  <select value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} data-testid="timeoff-time-from"
                    className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white [color-scheme:dark] focus:border-[#1a3fd1] focus:outline-none">
                    <option value="">—</option>
                    {TIME_OPTS.map((t) => <option key={t} value={t}>{fmtTime12(t)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/40">To time</label>
                  <select value={timeTo} onChange={(e) => setTimeTo(e.target.value)} data-testid="timeoff-time-to"
                    className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white [color-scheme:dark] focus:border-[#1a3fd1] focus:outline-none">
                    <option value="">—</option>
                    {TIME_OPTS.filter((t) => !timeFrom || t > timeFrom).map((t) => <option key={t} value={t}>{fmtTime12(t)}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
          <button
            onClick={submitTimeOff} disabled={busy}
            data-testid="timeoff-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarOff className="h-3.5 w-3.5" />} Send request
          </button>
        </div>

        {mine.length > 0 && (
          <div className="mt-4" data-testid="timeoff-mine">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/45">My requests</p>
            <ul className="space-y-1.5">
              {mine.map((r) => (
                <li key={r.request_id} data-testid={`timeoff-mine-${r.request_id}`} className="flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
                  <span className="font-mono text-white/80">{fmtDay(r.date_from)}{r.date_to !== r.date_from ? ` → ${fmtDay(r.date_to)}` : ""}{r.partial ? ` · ${fmtTime12(r.time_from)}–${fmtTime12(r.time_to)}` : ""}</span>
                  <span className="min-w-0 flex-1 truncate text-white/60">{r.reason}</span>
                  <StatusBadge status={r.status} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </CollapsibleCard>

      {/* Request vacation pay — cashes out banked vacation pay on an
          upcoming pay. Submitted by Sunday 11:59pm Toronto → this pay;
          later → next pay. Shows up yellow on the Payroll tab. */}
      <CollapsibleCard
        testid="vacation-request-card"
        icon={<Palmtree className="h-5 w-5" />}
        iconClass="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        title="Request vacation pay"
        subtitle="Cash out banked vacation pay on an upcoming pay"
      >
        {!vacInfo ? (
          <div className="flex items-center gap-2 text-sm text-white/50"><Loader2 className="h-4 w-4 animate-spin" /> Checking your balance…</div>
        ) : vacInfo.unavailable ? (
          <p className="text-sm text-white/60">Your account isn't linked to an employee record — ask the office to link it.</p>
        ) : (
          <div className="space-y-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">Banked vacation pay you can request</p>
              <p className="font-mono text-2xl font-bold text-white" data-testid="vacation-request-available">
                ${Number(vacInfo.availableAfterPending).toFixed(2)}
              </p>
              {vacInfo.availableAfterPending !== vacInfo.available && (
                <p className="text-[11px] text-white/40">${Number(vacInfo.available).toFixed(2)} banked minus pending requests</p>
              )}
              {vacInfo.hourlyRate > 0 && vacInfo.availableAfterPending > 0 && (
                <p className="text-[11px] text-white/50" data-testid="vacation-request-max-hours">
                  ≈ {(Number(vacInfo.availableAfterPending) / Number(vacInfo.hourlyRate)).toFixed(2)} hrs at ${Number(vacInfo.hourlyRate).toFixed(2)}/hr
                </p>
              )}
              {vacInfo.waiver && (
                <p className="theme-keep-bg mt-2 rounded-md border border-sky-300 bg-sky-100 px-3 py-2 text-[11px] leading-snug text-sky-900" data-testid="vacation-request-waiver-note">
                  <b>Vacation waiver on file:</b> your vacation pay is paid out on every cheque
                  {Number(vacInfo.weekAutoPaid) > 0 ? <> (${Number(vacInfo.weekAutoPaid).toFixed(2)} so far this week)</> : null}, so it never banks.
                  Only the <b>${Number(vacInfo.availableAfterPending).toFixed(2)}</b> already banked is requestable — anything above that would be refused, because your bank can't go negative.
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Dollars OR hours — hours are converted to dollars at your hourly rate */}
              <div className="inline-flex overflow-hidden rounded-md border border-white/15" data-testid="vacation-request-mode">
                {[["dollars", "$ Amount"], ["hours", "Hours"]].map(([m, label]) => (
                  <button key={m} type="button" onClick={() => { setVacMode(m); setVacAmt(""); }} aria-pressed={vacMode === m}
                    data-testid={`vacation-request-mode-${m}`}
                    className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-widest ${vacMode === m ? "bg-[#1a3fd1] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <span className="text-sm text-white/50">{vacMode === "hours" ? "hrs" : "$"}</span>
              <input
                type="number" min="0" step={vacMode === "hours" ? "0.25" : "0.01"} inputMode="decimal"
                value={vacAmt} onChange={(e) => setVacAmt(e.target.value)}
                placeholder={vacMode === "hours" ? "8" : "0.00"}
                data-testid="vacation-request-amount"
                className="h-10 w-32 rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
              />
              <button
                onClick={submitVacation} disabled={vacBusy}
                data-testid="vacation-request-submit"
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
              >
                {vacBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Palmtree className="h-3.5 w-3.5" />} Request payout
              </button>
            </div>
            <p className="text-[11px] text-white/40">
              Request by <b>Sunday 11:59 PM</b> to land on this week's pay — after that it rides the next one. Only vacation pay that's <b>banked</b> can be requested (vacation accruing on this week's hours counts{vacInfo.waiver ? " — except under a waiver, where it's paid out on each cheque instead" : ""}) — your bank can never go negative.
            </p>
            {(vacInfo.requests || []).length > 0 && (
              <div className="space-y-1.5" data-testid="vacation-request-list">
                {vacInfo.requests.map((r) => (
                  <div key={r.request_id} className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`vacation-request-${r.request_id}`}>
                    <p className="text-xs text-white/80">
                      <span className="font-mono font-semibold text-white">{Number(r.hours || 0) > 0 ? `${Number(r.hours).toFixed(2)} hrs (≈ $${Number(r.amount).toFixed(2)})` : `$${Number(r.amount).toFixed(2)}`}</span>
                      {" "}· week of {r.week_start} ·{" "}
                      <span className={`uppercase ${r.status === "pending" ? "text-amber-300" : r.status === "applied" ? "text-emerald-300" : "text-white/40 line-through"}`}>
                        {r.status === "applied" ? "added to pay" : r.status}
                      </span>
                    </p>
                    {r.status === "pending" && (
                      <button onClick={() => cancelVacation(r.request_id)} data-testid={`vacation-request-cancel-${r.request_id}`} className="text-[11px] text-red-300 underline underline-offset-2 hover:text-red-200">Cancel</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CollapsibleCard>

      {/* Expense reimbursement — anyone can submit; an admin approves before
          it lands on the job's books. The office is emailed with the receipt. */}
      <CollapsibleCard
        testid="expense-request-card"
        icon={<Receipt className="h-5 w-5" />}
        iconClass="border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300"
        title="Submit an expense"
        subtitle="Bought something for a job? Get reimbursed — office is emailed, admin approves"
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Description</label>
            <input value={exDesc} onChange={(e) => setExDesc(e.target.value)} placeholder="e.g. Pipe fittings from Home Depot" data-testid="expense-desc"
              className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Price ($)</label>
              <input type="number" min="0" step="0.01" value={exAmount} onChange={(e) => setExAmount(e.target.value)} placeholder="45.99" data-testid="expense-amount"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Date</label>
              <input type="date" value={exDate} onChange={(e) => setExDate(e.target.value)} data-testid="expense-date"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Job</label>
            <select value={exProjectId} onChange={(e) => setExProjectId(e.target.value)} data-testid="expense-project"
              className="h-10 w-full rounded-md border border-white/15 bg-[#0a0a0a] px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none">
              <option value="">— Select the job —</option>
              {exJobs.map((j) => (
                <option key={j.project_id} value={j.project_id}>{j.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Receipt photo</label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-white/20 bg-white/5 px-3 text-sm text-white/60 hover:border-[#1a3fd1]" data-testid="expense-receipt-upload-label">
                <Upload className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">Upload</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="expense-receipt-upload"
                  onChange={(e) => pickExpenseReceipt(e.target.files?.[0])} />
              </label>
              <label className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-white/20 bg-white/5 px-3 text-sm text-white/60 hover:border-[#1a3fd1]" data-testid="expense-receipt-camera-label">
                <Camera className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">Take photo</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="expense-receipt-camera"
                  onChange={(e) => pickExpenseReceipt(e.target.files?.[0])} />
              </label>
            </div>
            {exScanning && (
              <p className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0]" data-testid="expense-scanning">
                <Loader2 className="h-3 w-3 animate-spin" /> Reading receipt with AI…
              </p>
            )}
            {!exScanning && exScanned && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300" data-testid="expense-scanned-note">
                Details auto-filled from the receipt — edit anything that&apos;s off
              </p>
            )}
            {exReceipt && (
              <div className="mt-2 flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5" data-testid="expense-receipt-preview">
                {exReceipt.type?.startsWith("image/") && (
                  <img src={URL.createObjectURL(exReceipt)} alt="" className="h-10 w-10 shrink-0 rounded border border-white/10 object-cover" />
                )}
                <span className="min-w-0 flex-1 truncate text-xs text-white/70">{exReceipt.name}</span>
                <button onClick={() => setExReceipt(null)} aria-label="Remove receipt" data-testid="expense-receipt-remove"
                  className="rounded border border-white/15 bg-white/5 p-1 text-white/60 hover:text-red-400">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
          <button
            onClick={submitExpense} disabled={exBusy}
            data-testid="expense-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60"
          >
            {exBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Receipt className="h-3.5 w-3.5" />} Submit expense
          </button>
        </div>

        {exMine.length > 0 && (
          <div className="mt-4" data-testid="expense-mine">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/45">My submitted expenses</p>
            <ul className="space-y-1.5">
              {exMine.map((r) => (
                <li key={r.request_id} data-testid={`expense-mine-${r.request_id}`} className="flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
                  <span className="font-mono text-white/80">{fmtDay(r.date)}</span>
                  <span className="font-mono font-semibold text-white/90">${Number(r.amount).toFixed(2)}</span>
                  <span className="text-white/60">{r.project_name}</span>
                  <span className="min-w-0 flex-1 truncate text-white/60">{r.description}</span>
                  <StatusBadge status={r.status} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </CollapsibleCard>

      {/* Health benefit claim — nothing renders until the feature flag is
          known (prevents flashing the full card before the "off" note);
          when off, the whole card is replaced by just the note. */}
      {features === null ? null : features.health_benefits_enabled === false ? (
        <p className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-4 text-center text-sm text-white/60" data-testid="health-benefits-disabled-msg">
          Sorry, health benefits are not currently being offered.
        </p>
      ) : (
      <CollapsibleCard
        testid="health-claim-card"
        icon={<HeartPulse className="h-5 w-5" />}
        iconClass="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        title="Health benefit claim"
        subtitle="Brock claim form auto-filled + emailed to the office with your receipt"
      >
        {benefits && (
          <p className="rounded-md border border-emerald-500/25 bg-emerald-500/[0.06] px-3 py-2 font-mono text-[11px] text-emerald-700 dark:text-emerald-200" data-testid="benefits-summary">
            {benefits.allowance
              ? <>Benefits {benefits.year}: <span className="font-semibold">${Number(benefits.used).toFixed(2)} used</span> · <span className="font-semibold">${Number(benefits.remaining).toFixed(2)} left</span> of ${Number(benefits.allowance).toFixed(2)}</>
              : <>Benefits {benefits.year}: ${Number(benefits.used).toFixed(2)} claimed · no annual allowance set yet</>}
          </p>
        )}
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Name (patient)</label>
              <input value={hcName} onChange={(e) => setHcName(e.target.value)} data-testid="claim-name"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Service date</label>
              <input type="date" value={hcDate} onChange={(e) => setHcDate(e.target.value)} data-testid="claim-date"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Description</label>
            <input value={hcDesc} onChange={(e) => setHcDesc(e.target.value)} placeholder="e.g. Dental — check up & cleaning" data-testid="claim-desc"
              className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Amount ($)</label>
              <input type="number" min="0" step="0.01" value={hcAmount} onChange={(e) => setHcAmount(e.target.value)} placeholder="155.00" data-testid="claim-amount"
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Receipt (photo or PDF)</label>
              <label className="flex h-10 w-full cursor-pointer items-center gap-2 rounded-md border border-dashed border-white/20 bg-white/5 px-3 text-sm text-white/60 hover:border-[#1a3fd1]" data-testid="claim-receipt-label">
                <Paperclip className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{hcReceipt ? hcReceipt.name : "Choose file…"}</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="claim-receipt"
                  onChange={(e) => setHcReceipt(e.target.files?.[0] || null)} />
              </label>
            </div>
          </div>
          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Your signature — goes on the claim form's signature line</label>
            <SignaturePad key={hcSigKey} open onChange={setHcSig} testid="claim-signature-pad" />
            <p className="mt-1 text-[11px] text-white/40">By signing you certify that all health services were purchased by or for an eligible member of your household.</p>
          </div>
          <button
            onClick={submitHealthClaim} disabled={hcBusy}
            data-testid="claim-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
          >
            {hcBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Submit claim
          </button>
        </div>
      </CollapsibleCard>
      )}

      {/* Rip-out report — anyone can log one from the field; the form opens
          inline like every other box on this page */}
      <CollapsibleCard
        testid="ripout-card"
        icon={<Camera className="h-5 w-5" />}
        iconClass="border-red-500/40 bg-red-500/10 text-red-500 dark:text-red-300"
        title="Log a rip-out"
        subtitle="Another trade tore out our work? Photos + details go straight to the office"
      >
        <RipOutFormDialog inline testid="ripout-form" />
      </CollapsibleCard>

      {/* General request */}
      <CollapsibleCard
        testid="general-request-card"
        icon={<Send className="h-5 w-5" />}
        iconClass="border-white/20 bg-white/5 text-white/60"
        title="General request"
        subtitle="Anything else — emailed straight to the office"
      >
        <textarea
          value={genMsg}
          onChange={(e) => setGenMsg(e.target.value)}
          rows={3}
          placeholder="Type your request…"
          data-testid="general-request-message"
          className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
        />
        <button
          onClick={submitGeneral} disabled={genBusy}
          data-testid="general-request-submit"
          className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60"
        >
          {genBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send request
        </button>
      </CollapsibleCard>

      {isAdmin && (
        <>
          {/* Letter for any employee — owner/admin only, hidden from managers */}
          {canGenLetters && (
          <CollapsibleCard
            testid="letter-anyone"
            icon={<FileText className="h-5 w-5" />}
            iconClass="border-[#1a3fd1]/40 bg-[#1a3fd1]/10 text-[#4f6fe8]"
            title="Employment letter for any employee"
            subtitle="Admin/owner — pick an employee, download their letter"
          >
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={letterEmp}
                onChange={(e) => setLetterEmp(e.target.value)}
                data-testid="letter-employee-select"
                className="h-10 min-w-0 flex-1 rounded-md border border-white/15 bg-[#0a0a0a] px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none sm:max-w-xs"
              >
                <option value="">— Pick an employee —</option>
                {employees.map((e) => (
                  <option key={e.employee_id} value={e.employee_id}>{e.name}</option>
                ))}
              </select>
              <button
                onClick={() => letterEmp && downloadLetterFor(letterEmp)}
                disabled={!letterEmp || downloading}
                data-testid="letter-anyone-download"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60"
              >
                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download PDF
              </button>
            </div>
          </CollapsibleCard>
          )}
          {/* Expense reimbursement queue — approving adds the expense to the job */}
          <CollapsibleCard
            testid="expense-queue"
            count={exQueue.filter((r) => r.status === "pending").length}
            icon={<Receipt className="h-5 w-5" />}
            iconClass="border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300"
            title="Expense reimbursements"
            subtitle="Approving adds it to the job's books (tagged as an employee reimbursement) — the office email already has the receipt"
          >
            {exQueue.filter((r) => r.status === "pending").length === 0 ? (
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="expense-queue-empty">No pending expenses</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {exQueue.filter((r) => r.status === "pending").map((r) => (
                  <li key={r.request_id} data-testid={`expense-queue-${r.request_id}`} onClick={() => openExpenseDetail(r)}
                    className="flex cursor-pointer flex-wrap items-center gap-2 rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-xs hover:border-sky-400/50">
                    <span className="font-semibold text-white">{r.employee_name}</span>
                    <span className="font-mono text-white/80">{fmtDay(r.date)}</span>
                    <span className="font-mono font-semibold text-white/90">${Number(r.amount).toFixed(2)}</span>
                    <span className="text-white/60">{r.project_name}</span>
                    <span className="min-w-0 flex-1 truncate text-white/60">{r.description}</span>
                    <button onClick={(e) => { e.stopPropagation(); viewExpenseReceipt(r); }} data-testid={`expense-receipt-view-${r.request_id}`}
                      className="rounded border border-white/15 bg-white/5 px-2 py-1 font-mono text-[10px] text-sky-700 dark:text-sky-300 hover:border-white/25">
                      receipt
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); resolveExpense(r, true); }} disabled={busy} data-testid={`expense-approve-${r.request_id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 font-semibold text-white hover:bg-emerald-500 disabled:opacity-60">
                      <Check className="h-3 w-3" /> Approve
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); resolveExpense(r, false); }} disabled={busy} data-testid={`expense-deny-${r.request_id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 font-semibold text-white hover:bg-red-500 disabled:opacity-60">
                      <X className="h-3 w-3" /> Deny
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {exQueue.filter((r) => r.status !== "pending").length > 0 && (
              <div className="mt-4">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/45">History</p>
                <ul className="space-y-1.5">
                  {exQueue.filter((r) => r.status !== "pending").slice(0, 25).map((r) => (
                    <li key={r.request_id} data-testid={`expense-history-${r.request_id}`} onClick={() => openExpenseDetail(r)}
                      className="flex cursor-pointer flex-wrap items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs hover:border-white/25">
                      <span className="font-semibold text-white">{r.employee_name}</span>
                      <span className="font-mono text-white/80">{fmtDay(r.date)}</span>
                      <span className="font-mono text-white/90">${Number(r.amount).toFixed(2)}</span>
                      <span className="text-white/60">{r.project_name}</span>
                      <span className="min-w-0 flex-1 truncate text-white/60">{r.description}</span>
                      {r.resolved_by_name && <span className="font-mono text-[10px] text-white/40">by {r.resolved_by_name}</span>}
                      {r.paid && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-300" data-testid={`expense-paid-${r.request_id}`}>Paid</span>}
                      <StatusBadge status={r.status} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CollapsibleCard>

          {/* Approval queue */}
          <CollapsibleCard
            testid="timeoff-queue"
            count={pending.length}
            icon={<CalendarOff className="h-5 w-5" />}
            iconClass="border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300"
            title="Time-off requests"
            subtitle="Approving marks the days BOOKED OFF on the Hours grid"
          >
            {pending.length === 0 ? (
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="timeoff-queue-empty">No pending requests</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {pending.map((r) => (
                  <li key={r.request_id} data-testid={`timeoff-queue-${r.request_id}`} className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs">
                    <span className="font-semibold text-white">{r.employee_name}</span>
                    <span className="font-mono text-white/80">{fmtDay(r.date_from)}{r.date_to !== r.date_from ? ` → ${fmtDay(r.date_to)}` : ""}{r.partial ? ` · ${fmtTime12(r.time_from)}–${fmtTime12(r.time_to)}` : ""}</span>
                    <span className="min-w-0 flex-1 truncate text-white/60">{r.reason}</span>
                    <button onClick={() => resolve(r, true)} disabled={busy} data-testid={`timeoff-approve-${r.request_id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 font-semibold text-white hover:bg-emerald-500 disabled:opacity-60">
                      <Check className="h-3 w-3" /> Approve
                    </button>
                    <button onClick={() => resolve(r, false)} disabled={busy} data-testid={`timeoff-deny-${r.request_id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 font-semibold text-white hover:bg-red-500 disabled:opacity-60">
                      <X className="h-3 w-3" /> Deny
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {history.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/45">History</p>
                <ul className="space-y-1.5">
                  {history.slice(0, 25).map((r) => (
                    <li key={r.request_id} data-testid={`timeoff-history-${r.request_id}`} className="flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
                      <span className="font-semibold text-white">{r.employee_name}</span>
                      <span className="font-mono text-white/80">{fmtDay(r.date_from)}{r.date_to !== r.date_from ? ` → ${fmtDay(r.date_to)}` : ""}{r.partial ? ` · ${fmtTime12(r.time_from)}–${fmtTime12(r.time_to)}` : ""}</span>
                      <span className="min-w-0 flex-1 truncate text-white/60">{r.reason}</span>
                      {r.resolved_by_name && <span className="font-mono text-[10px] text-white/40">by {r.resolved_by_name}</span>}
                      <StatusBadge status={r.status} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CollapsibleCard>

          {/* Vacation pay payout requests — team queue (owner/admin/superadmin).
              The same list shows yellow on the Payroll tab; the money is
              entered there in the employee's vacation payout field. */}
          {["owner", "admin", "superadmin"].includes(user?.role) && (
            <CollapsibleCard
              testid="vacation-queue"
              count={vacQueue.length}
              icon={<Palmtree className="h-5 w-5" />}
              iconClass="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              title="Vacation pay requests"
              subtitle="Banked vacation pay employees want cashed out — enter it in their vacation payout on the Payroll tab"
            >
              {vacQueue.length === 0 ? (
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="vacation-queue-empty">No pending vacation pay requests</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {vacQueue.map((r) => (
                    <li key={r.request_id} data-testid={`vacation-queue-${r.request_id}`} className="flex flex-wrap items-center gap-2 rounded-md border border-yellow-400/40 bg-yellow-300/10 px-3 py-2 text-xs">
                      <span className="font-semibold text-white">{r.employee_name}</span>
                      <span className="font-mono font-semibold text-white">{Number(r.hours || 0) > 0 ? `${Number(r.hours).toFixed(2)} hrs (≈ $${Number(r.amount).toFixed(2)})` : `$${Number(r.amount).toFixed(2)}`}</span>
                      <span className="min-w-0 flex-1 text-white/60">pay week of {fmtDay(r.week_start)} · requested {fmtDay((r.created_at || "").slice(0, 10))}</span>
                      <button onClick={() => cancelVacation(r.request_id)} disabled={busy} data-testid={`vacation-queue-delete-${r.request_id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 font-semibold text-white hover:bg-red-500 disabled:opacity-60">
                        <X className="h-3 w-3" /> Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CollapsibleCard>
          )}

          {/* Overtime requests (8-hour daily cap) */}
          <CollapsibleCard
            testid="overtime-queue"
            count={otQueue.filter((r) => r.status === "pending").length}
            icon={<AlarmClockCheck className="h-5 w-5" />}
            iconClass="border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300"
            title="Overtime requests"
            subtitle="At 8 paid hours the employee's paid clock pauses while you decide — approving pays the wait, denying clocks them out on the spot"
          >
            {otQueue.filter((r) => r.status === "pending").length === 0 ? (
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="overtime-queue-empty">No pending overtime requests</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {otQueue.filter((r) => r.status === "pending").map((r) => (
                  <li key={r.request_id} data-testid={`overtime-queue-${r.request_id}`} className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs">
                    <span className="font-semibold text-white">{r.employee_name}</span>
                    <span className="font-mono text-white/80">{fmtDay(r.day)}</span>
                    <span className="min-w-0 flex-1 text-white/60">hit 8 paid hours — still on the clock</span>
                    <button onClick={() => resolveOt(r, true)} disabled={busy} data-testid={`overtime-approve-${r.request_id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 font-semibold text-white hover:bg-emerald-500 disabled:opacity-60">
                      <Check className="h-3 w-3" /> Approve
                    </button>
                    <button onClick={() => resolveOt(r, false)} disabled={busy} data-testid={`overtime-deny-${r.request_id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 font-semibold text-white hover:bg-red-500 disabled:opacity-60">
                      <X className="h-3 w-3" /> Deny
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {otQueue.filter((r) => r.status !== "pending").length > 0 && (
              <div className="mt-4">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/45">History</p>
                <ul className="space-y-1.5">
                  {otQueue.filter((r) => r.status !== "pending").slice(0, 25).map((r) => (
                    <li key={r.request_id} data-testid={`overtime-history-${r.request_id}`} className="flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
                      <span className="font-semibold text-white">{r.employee_name}</span>
                      <span className="font-mono text-white/80">{fmtDay(r.day)}</span>
                      <span className="min-w-0 flex-1" />
                      {r.resolved_by_name && <span className="font-mono text-[10px] text-white/40">by {r.resolved_by_name}</span>}
                      <StatusBadge status={r.status} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CollapsibleCard>

          {/* Punch request log */}
          <CollapsibleCard
            testid="punch-request-log"
            icon={<History className="h-5 w-5" />}
            iconClass="border-white/20 bg-white/5 text-white/60"
            title="Punch request log"
            subtitle="Employer punch requests (Oops clock-ins) and how each was resolved"
          >
            {punchLog.length === 0 ? (
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="punch-log-empty">No punch requests yet</p>
            ) : (
              <ul className="mt-3 space-y-1.5">
                {punchLog.map((p) => (
                  <li key={p.entry_id} data-testid={`punch-log-${p.entry_id}`} className="flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
                    <span className="font-semibold text-white">{p.employee_name}</span>
                    <span className="font-mono text-white/80">claimed {fmtTs(p.claimed_at)}</span>
                    {p.corrected_to && <span className="font-mono text-[#4f6fe8]">→ {fmtTs(p.corrected_to)}</span>}
                    <span className="min-w-0 flex-1" />
                    {p.resolved_by && <span className="font-mono text-[10px] text-white/40">by {p.resolved_by}</span>}
                    <StatusBadge status={p.status} />
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleCard>
        </>
      )}

      <ReceiptViewer src={exReceiptSrc} onClose={() => setExReceiptSrc(null)} />
      {/* expense details — any row click opens this; superadmin gets Mark paid */}
      {exDetail && createPortal(
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-3" data-testid="expense-detail-dialog" onClick={() => setExDetail(null)}>
          <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-300">
                  <Receipt className="h-4 w-4" />
                </div>
                <h3 className="text-base font-bold uppercase tracking-tight">Expense request</h3>
              </div>
              <button onClick={() => setExDetail(null)} aria-label="Close" data-testid="expense-detail-close" className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 overflow-y-auto p-4 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold" data-testid="expense-detail-employee">{exDetail.employee_name}</span>
                <StatusBadge status={exDetail.status} />
                {exDetail.paid && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-300" data-testid="expense-detail-paid-badge">Paid</span>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Amount</p><p className="font-mono text-base font-bold" data-testid="expense-detail-amount">${Number(exDetail.amount).toFixed(2)}</p></div>
                <div><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Date</p><p className="mt-0.5" data-testid="expense-detail-date">{fmtDay(exDetail.date)}</p></div>
                <div className="col-span-2"><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Job</p><p className="mt-0.5" data-testid="expense-detail-project">{exDetail.project_name}</p></div>
                <div className="col-span-2"><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Description</p><p className="mt-0.5 whitespace-pre-wrap" data-testid="expense-detail-description">{exDetail.description}</p></div>
                {exDetail.resolved_by_name && (
                  <div className="col-span-2"><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">{exDetail.status === "approved" ? "Approved" : "Handled"} by</p><p className="mt-0.5 text-white/70">{exDetail.resolved_by_name}{exDetail.resolved_at ? ` · ${fmtDay(exDetail.resolved_at.slice(0, 10))}` : ""}</p></div>
                )}
                {exDetail.paid && exDetail.paid_at && (
                  <div className="col-span-2"><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Reimbursement sent</p><p className="mt-0.5 text-emerald-600 dark:text-emerald-300" data-testid="expense-detail-paid-at">{fmtDay(exDetail.paid_at.slice(0, 10))}</p></div>
                )}
              </div>
              <div>
                <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-white/40">Receipt</p>
                {exDetailReceipt === undefined && <p className="text-xs text-white/40">Loading…</p>}
                {exDetailReceipt === null && <p className="text-xs text-white/40" data-testid="expense-detail-no-receipt">No receipt on file.</p>}
                {exDetailReceipt && (
                  <img src={exDetailReceipt} alt="Receipt" onClick={() => setExReceiptSrc(exDetailReceipt)}
                    className="max-h-64 cursor-zoom-in rounded-md border border-white/10 object-contain" data-testid="expense-detail-receipt" />
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2 border-t border-white/10 pt-3">
                {exDetail.status === "pending" && (
                  <>
                    <button onClick={() => { resolveExpense(exDetail, true); setExDetail(null); }} disabled={busy} data-testid="expense-detail-approve"
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"><Check className="h-3 w-3" /> Approve</button>
                    <button onClick={() => { resolveExpense(exDetail, false); setExDetail(null); }} disabled={busy} data-testid="expense-detail-deny"
                      className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-60"><X className="h-3 w-3" /> Deny</button>
                  </>
                )}
                {isSuper && exDetail.status === "approved" && !exDetail.paid && (
                  <button onClick={() => markExpensePaid(exDetail, true)} disabled={busy} data-testid="expense-detail-mark-paid"
                    className="inline-flex items-center gap-1 rounded-md bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-60"><Check className="h-3 w-3" /> Mark as paid</button>
                )}
                {isSuper && exDetail.status === "approved" && exDetail.paid && (
                  <button onClick={() => markExpensePaid(exDetail, false)} disabled={busy} data-testid="expense-detail-unmark-paid"
                    className="rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/70 hover:bg-white/10 disabled:opacity-60">Remove paid stamp</button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RequestsTab;
